/* ==========================================================================
   Product Video ADA — Accessibility controls for product media videos.

   Finds product video players in the media gallery, injects CC/AD/transcript
   controls, and handles VTT captions overlay and alt video swapping.

   Handles Shopify deferred-media pattern where <video> doesn't exist until
   the user clicks play.

   Reads config from #product-video-ada-config-{productId} JSON block
   rendered by the product-video-ada.liquid snippet.
   ========================================================================== */

(function () {
  'use strict';

  var DEBOUNCE_MS = 300;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var configs = document.querySelectorAll('[id^="product-video-ada-config-"]');
    for (var i = 0; i < configs.length; i++) {
      try {
        var config = JSON.parse(configs[i].textContent);
        console.log('[Product Video ADA] Config loaded:', config);
        initProductVideoAda(config);
      } catch (e) {
        console.error('[Product Video ADA] Config parse error:', e);
      }
    }
  });

  function initProductVideoAda(config) {
    var attached = new WeakSet();

    function tryAttach() {
      var containers = findProductVideoContainers();
      console.log('[Product Video ADA] Found', containers.length, 'video container(s)');

      for (var i = 0; i < containers.length; i++) {
        if (!attached.has(containers[i]) && !containers[i].querySelector('.dt-product-ada-wrapper')) {
          attached.add(containers[i]);
          attachControls(containers[i], config);
        }
      }
      return containers.length;
    }

    /* Try immediately */
    tryAttach();

    /* Also observe for dynamically loaded content */
    var observer = new MutationObserver(function () { tryAttach(); });
    observer.observe(document.body, { childList: true, subtree: true });

    /* Recheck periodically for first 15s */
    var checks = 0;
    var interval = setInterval(function () {
      checks++;
      tryAttach();
      if (checks >= 30) {
        clearInterval(interval);
        observer.disconnect();
      }
    }, 500);
  }

  /* ------------------------------------------------------------------
     Find product video containers
     ------------------------------------------------------------------ */

  function findProductVideoContainers() {
    var containers = [];
    var seen = new WeakSet();

    /* Symmetry selectors for product video media */
    var selectors = [
      '.product-media--video',
      '[data-media-type="video"]',
      '.product-media[data-media-type="video"]'
    ];

    for (var s = 0; s < selectors.length; s++) {
      var els = document.querySelectorAll(selectors[s]);
      for (var i = 0; i < els.length; i++) {
        if (!seen.has(els[i])) {
          seen.add(els[i]);
          containers.push(els[i]);
        }
      }
    }

    /* Fallback: find video-component or deferred-media inside product areas */
    if (containers.length === 0) {
      var productAreas = document.querySelectorAll(
        '.product-area, .product-section, [data-section-type="product"], main'
      );
      for (var p = 0; p < productAreas.length; p++) {
        var comps = productAreas[p].querySelectorAll('video-component, deferred-media');
        for (var c = 0; c < comps.length; c++) {
          var parent = comps[c].closest('.product-media') || comps[c].parentElement;
          if (parent && !seen.has(parent)) {
            seen.add(parent);
            containers.push(parent);
          }
        }
      }
    }

    return containers;
  }

  /* ------------------------------------------------------------------
     Attach ADA controls to a video container
     ------------------------------------------------------------------ */

  function attachControls(container, config) {
    console.log('[Product Video ADA] Attaching controls to:', container);

    /* Ensure relative positioning */
    var style = window.getComputedStyle(container);
    if (style.position === 'static') {
      container.style.position = 'relative';
    }

    var hasCc = !!(config.vttUrl || config.ccUrl);
    var hasAd = !!config.adUrl;
    var hasTranscript = !!config.hasTranscript;
    var ccMode = config.ccUrl ? 'swap' : (config.vttUrl ? 'vtt' : null);

    console.log('[Product Video ADA] Features — CC:', hasCc, '(' + ccMode + ') | AD:', hasAd, '| Transcript:', hasTranscript);

    /* State */
    var ccActive = false;
    var adActive = false;
    var vttCues = null;
    var vttLoading = false;
    var boundVideos = [];
    var lastCcClick = 0;
    var lastAdClick = 0;
    var lastTranscriptClick = 0;

    var ccVideoWrap = null;
    var adVideoWrap = null;
    var captionOverlay = null;
    var captionText = null;

    /* ------------------------------------------------------------------
       Build and inject controls HTML
       ------------------------------------------------------------------ */

    var controlsHtml = '<div class="dt-a11y-controls dt-a11y-controls--' +
      (config.controlsLayout || 'horizontal') +
      '" role="toolbar" aria-label="Video accessibility controls">';

    if (hasCc) {
      controlsHtml += '<button type="button" class="dt-a11y-btn dt-a11y-btn--label" role="switch" ' +
        'aria-checked="false" aria-label="Closed captions" data-dt-product-cc>' +
        '<span class="dt-btn-text" aria-hidden="true">CC</span>' +
        '<span class="dt-tooltip" role="tooltip">Captions (C)</span></button>';
    }

    if (hasAd) {
      controlsHtml += '<button type="button" class="dt-a11y-btn dt-a11y-btn--label" role="switch" ' +
        'aria-checked="false" aria-label="Audio description" data-dt-product-ad>' +
        '<span class="dt-btn-text" aria-hidden="true">AD</span>' +
        '<span class="dt-tooltip" role="tooltip">Audio description (D)</span></button>';
    }

    if (hasTranscript) {
      controlsHtml += '<button type="button" class="dt-a11y-btn" role="switch" ' +
        'aria-checked="false" aria-controls="' + config.transcriptId + '" ' +
        'aria-label="Transcript" data-dt-product-transcript>' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
        '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>' +
        '<polyline points="14,2 14,8 20,8"/>' +
        '<line x1="16" y1="13" x2="8" y2="13"/>' +
        '<line x1="16" y1="17" x2="8" y2="17"/></svg>' +
        '<span class="dt-tooltip" role="tooltip">Transcript (T)</span></button>';
    }

    controlsHtml += '</div>';
    controlsHtml += '<div class="dt-a11y-live" aria-live="polite" aria-atomic="true"></div>';

    var wrapper = document.createElement('div');
    wrapper.className = 'dt-product-ada-wrapper';
    wrapper.innerHTML = controlsHtml;
    container.appendChild(wrapper);

    /* ------------------------------------------------------------------
       Create alt video elements
       ------------------------------------------------------------------ */

    if (ccMode === 'vtt') {
      captionOverlay = document.createElement('div');
      captionOverlay.className = 'dt-caption-overlay';
      captionOverlay.setAttribute('aria-live', 'polite');
      captionOverlay.setAttribute('aria-atomic', 'true');
      captionOverlay.hidden = true;
      captionText = document.createElement('span');
      captionText.className = 'dt-caption-text';
      captionOverlay.appendChild(captionText);
      container.appendChild(captionOverlay);
    }

    if (ccMode === 'swap' && config.ccUrl) {
      ccVideoWrap = document.createElement('div');
      ccVideoWrap.className = 'dt-alt-video';
      ccVideoWrap.setAttribute('aria-hidden', 'true');
      ccVideoWrap.innerHTML = '<video playsinline controls preload="none" data-src="' + escapeAttr(config.ccUrl) + '"></video>';
      container.appendChild(ccVideoWrap);
    }

    if (hasAd && config.adUrl) {
      adVideoWrap = document.createElement('div');
      adVideoWrap.className = 'dt-alt-video';
      adVideoWrap.setAttribute('aria-hidden', 'true');
      adVideoWrap.innerHTML = '<video playsinline controls preload="none" data-src="' + escapeAttr(config.adUrl) + '"></video>';
      container.appendChild(adVideoWrap);
    }

    /* ------------------------------------------------------------------
       Get references
       ------------------------------------------------------------------ */

    var liveRegion = wrapper.querySelector('.dt-a11y-live');
    var ccBtn = wrapper.querySelector('[data-dt-product-cc]');
    var adBtn = wrapper.querySelector('[data-dt-product-ad]');
    var transcriptBtn = wrapper.querySelector('[data-dt-product-transcript]');

    var drawer = config.transcriptId ? document.getElementById(config.transcriptId) : null;
    var transcriptClose = drawer ? drawer.querySelector('[data-dt-product-transcript-close]') : null;

    console.log('[Product Video ADA] Elements — ccBtn:', !!ccBtn, '| adBtn:', !!adBtn,
      '| transcriptBtn:', !!transcriptBtn, '| drawer:', !!drawer);

    /* ------------------------------------------------------------------
       Reposition transcript drawer below the video area
       ------------------------------------------------------------------ */

    if (drawer) {
      /* Try to place after the full product layout (spans full width) */
      var productForm = container.closest('product-form, .js-product, .product-info');
      if (productForm) {
        productForm.parentNode.insertBefore(drawer, productForm.nextSibling);
      } else {
        /* Fallback: after media gallery */
        var mediaGallery = container.closest('media-gallery, .media-gallery');
        if (mediaGallery) {
          mediaGallery.parentNode.insertBefore(drawer, mediaGallery.nextSibling);
        } else {
          container.parentNode.insertBefore(drawer, container.nextSibling);
        }
      }
    }

    /* ------------------------------------------------------------------
       Helpers: find video elements
       ------------------------------------------------------------------ */

    function getPrimaryVideo() {
      /* Look for a real <video> that isn't one of our alt videos */
      var videos = container.querySelectorAll('video');
      for (var v = 0; v < videos.length; v++) {
        if (!videos[v].closest('.dt-alt-video')) {
          return videos[v];
        }
      }
      return null;
    }

    function getActiveVideo() {
      if (adActive && adVideoWrap) {
        var v = adVideoWrap.querySelector('video');
        if (v) return v;
      }
      if (ccActive && ccVideoWrap) {
        var v = ccVideoWrap.querySelector('video');
        if (v) return v;
      }
      return getPrimaryVideo();
    }

    function showAltVideo(altWrap, primaryVideo) {
      var altVideo = altWrap.querySelector('video');
      if (!altVideo) return;

      /* Load src on first use */
      if (!altVideo.getAttribute('src') && altVideo.dataset.src) {
        altVideo.src = altVideo.dataset.src;
      }

      altWrap.classList.add('dt-active');
      altWrap.setAttribute('aria-hidden', 'false');

      if (primaryVideo) {
        try { altVideo.currentTime = primaryVideo.currentTime || 0; } catch (e) {}
        primaryVideo.pause();
      }

      altVideo.play().catch(function (err) {
        console.log('[Product Video ADA] Alt video play failed:', err.message);
      });
    }

    function showAltVideoDirect(altWrap) {
      var altVideo = altWrap.querySelector('video');
      if (!altVideo) return;

      /* Load src on first use */
      if (!altVideo.getAttribute('src') && altVideo.dataset.src) {
        altVideo.src = altVideo.dataset.src;
      }

      altWrap.classList.add('dt-active');
      altWrap.setAttribute('aria-hidden', 'false');
      altVideo.play().catch(function (err) {
        console.log('[Product Video ADA] Direct play failed:', err.message);
      });
    }

    function hideAltVideo(altWrap) {
      var altVideo = altWrap.querySelector('video');
      if (altVideo) {
        altVideo.pause();
        altVideo.currentTime = 0;
      }
      altWrap.classList.remove('dt-active');
      altWrap.setAttribute('aria-hidden', 'true');
    }

    function announce(msg) {
      if (!liveRegion) return;
      liveRegion.textContent = '';
      requestAnimationFrame(function () { liveRegion.textContent = msg; });
    }

    /* ------------------------------------------------------------------
       VTT
       ------------------------------------------------------------------ */

    function loadAndBindVTT(video) {
      if (vttCues) { bindTimeUpdate(video); return; }
      if (vttLoading || !config.vttUrl) return;

      vttLoading = true;
      fetch(config.vttUrl)
        .then(function (res) {
          if (!res.ok) throw new Error('VTT fetch failed: ' + res.status);
          return res.text();
        })
        .then(function (raw) {
          vttCues = parseVTT(raw);
          console.log('[Product Video ADA] Parsed', vttCues.length, 'cues');
          bindTimeUpdate(video);
        })
        .catch(function (err) {
          console.error('[Product Video ADA] VTT error:', err);
          vttLoading = false;
        });
    }

    function bindTimeUpdate(video) {
      for (var b = 0; b < boundVideos.length; b++) {
        if (boundVideos[b] === video) return;
      }
      boundVideos.push(video);

      video.addEventListener('timeupdate', function () {
        if (!ccActive || !vttCues || !captionText) return;
        var active = getActiveVideo();
        if (active !== video) return;

        var t = video.currentTime;
        var activeCue = null;
        for (var i = 0; i < vttCues.length; i++) {
          if (t >= vttCues[i].start && t < vttCues[i].end) { activeCue = vttCues[i]; break; }
        }
        captionText.textContent = activeCue ? activeCue.text : '';
      });
    }

    /* ------------------------------------------------------------------
       Transcript
       ------------------------------------------------------------------ */

    function openTranscript() {
      if (!drawer) return;
      drawer.style.setProperty('display', 'block', 'important');
      drawer.setAttribute('aria-hidden', 'false');
      if (transcriptBtn) transcriptBtn.setAttribute('aria-checked', 'true');
      announce('Transcript opened');
    }

    function closeTranscript() {
      if (!drawer) return;
      drawer.style.setProperty('display', 'none', 'important');
      drawer.setAttribute('aria-hidden', 'true');
      if (transcriptBtn) transcriptBtn.setAttribute('aria-checked', 'false');
      announce('Transcript closed');
    }

    if (transcriptBtn && drawer) {
      transcriptBtn.addEventListener('click', function (e) {
        e.stopPropagation(); e.preventDefault();
        var now = Date.now();
        if (now - lastTranscriptClick < DEBOUNCE_MS) return;
        lastTranscriptClick = now;
        var isOn = transcriptBtn.getAttribute('aria-checked') === 'true';
        if (isOn) closeTranscript(); else openTranscript();
      });
    } else if (transcriptBtn && !drawer) {
      console.warn('[Product Video ADA] Transcript button exists but drawer not found:', config.transcriptId);
    }

    if (transcriptClose && drawer) {
      transcriptClose.addEventListener('click', function (e) {
        e.stopPropagation(); e.preventDefault();
        closeTranscript();
        if (transcriptBtn) transcriptBtn.focus();
      });
    }

    /* ------------------------------------------------------------------
       CC Toggle
       ------------------------------------------------------------------ */

    if (ccBtn) {
      ccBtn.addEventListener('click', function (e) {
        e.stopPropagation(); e.preventDefault();
        var now = Date.now();
        if (now - lastCcClick < DEBOUNCE_MS) return;
        lastCcClick = now;

        ccActive = !ccActive;
        ccBtn.setAttribute('aria-checked', String(ccActive));
        console.log('[Product Video ADA] CC toggled:', ccActive, '| mode:', ccMode);

        var primaryVideo = getPrimaryVideo();
        console.log('[Product Video ADA] Primary video found:', !!primaryVideo);

        if (ccMode === 'swap' && ccVideoWrap) {
          if (ccActive) {
            if (primaryVideo) {
              showAltVideo(ccVideoWrap, primaryVideo);
            } else {
              showAltVideoDirect(ccVideoWrap);
            }
            if (adActive) {
              adActive = false;
              if (adBtn) adBtn.setAttribute('aria-checked', 'false');
              if (adVideoWrap) hideAltVideo(adVideoWrap);
            }
          } else {
            hideAltVideo(ccVideoWrap);
            if (primaryVideo) primaryVideo.play().catch(function () {});
          }
        } else if (ccMode === 'vtt') {
          if (ccActive) {
            if (captionOverlay) captionOverlay.removeAttribute('hidden');
            var activeVid = getActiveVideo();
            if (activeVid) loadAndBindVTT(activeVid);
          } else {
            if (captionOverlay) captionOverlay.setAttribute('hidden', '');
            if (captionText) captionText.textContent = '';
          }
        }

        announce(ccActive ? 'Closed captions on' : 'Closed captions off');
      });
    }

    /* ------------------------------------------------------------------
       AD Toggle
       ------------------------------------------------------------------ */

    if (adBtn && adVideoWrap) {
      adBtn.addEventListener('click', function (e) {
        e.stopPropagation(); e.preventDefault();
        var now = Date.now();
        if (now - lastAdClick < DEBOUNCE_MS) return;
        lastAdClick = now;

        adActive = !adActive;
        adBtn.setAttribute('aria-checked', String(adActive));
        console.log('[Product Video ADA] AD toggled:', adActive);

        var primaryVideo = getPrimaryVideo();

        if (adActive) {
          if (primaryVideo) {
            showAltVideo(adVideoWrap, primaryVideo);
          } else {
            showAltVideoDirect(adVideoWrap);
          }

          if (ccMode === 'swap' && ccActive && ccVideoWrap) {
            ccActive = false;
            if (ccBtn) ccBtn.setAttribute('aria-checked', 'false');
            hideAltVideo(ccVideoWrap);
          } else if (ccMode === 'vtt' && ccActive) {
            var adVid = adVideoWrap.querySelector('video');
            if (adVid) loadAndBindVTT(adVid);
          }
        } else {
          hideAltVideo(adVideoWrap);
          if (primaryVideo) primaryVideo.play().catch(function () {});
          if (ccMode === 'vtt' && ccActive) {
            var pv = getPrimaryVideo();
            if (pv) loadAndBindVTT(pv);
          }
        }

        announce(adActive ? 'Audio description on' : 'Audio description off');
      });
    }

    /* ------------------------------------------------------------------
       Keyboard shortcuts
       ------------------------------------------------------------------ */

    container.addEventListener('keydown', function (e) {
      var tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      switch (e.key.toLowerCase()) {
        case 'c': if (ccBtn) { e.preventDefault(); ccBtn.click(); } break;
        case 'd': if (adBtn) { e.preventDefault(); adBtn.click(); } break;
        case 't': if (transcriptBtn) { e.preventDefault(); transcriptBtn.click(); } break;
      }
    });

    console.log('[Product Video ADA] ✓ Controls attached');
  }

  /* ======================================================================
     VTT Parser
     ====================================================================== */

  function parseVTT(raw) {
    var cues = [];
    var lines = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    var i = 0;
    while (i < lines.length && lines[i].indexOf('-->') === -1) i++;
    while (i < lines.length) {
      var line = lines[i].trim();
      if (line.indexOf('-->') !== -1) {
        var parts = line.split('-->');
        var start = parseTimestamp(parts[0].trim());
        var endStr = parts[1].trim().split(' ')[0];
        var end = parseTimestamp(endStr);
        var textLines = [];
        i++;
        while (i < lines.length && lines[i].trim() !== '') { textLines.push(lines[i].trim()); i++; }
        if (!isNaN(start) && !isNaN(end) && textLines.length > 0) {
          cues.push({ start: start, end: end, text: textLines.join('\n').replace(/<[^>]+>/g, '') });
        }
      } else { i++; }
    }
    return cues;
  }

  function parseTimestamp(str) {
    var parts = str.split(':');
    if (parts.length === 3) return parseFloat(parts[0]) * 3600 + parseFloat(parts[1]) * 60 + parseFloat(parts[2]);
    if (parts.length === 2) return parseFloat(parts[0]) * 60 + parseFloat(parts[1]);
    return NaN;
  }

  function escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

})();